import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { transferStock } from '@/app/services/inventoryService';
import { Godown, ItemStockSummaryResponse } from '@/app/types/inventory';
import { Item } from '@/app/types/item';

const stockTransferSchema = z.object({
  from_godown_id: z.number({
    required_error: 'Please select source godown',
  }),
  to_godown_id: z.number({
    required_error: 'Please select destination godown',
  }),
  quantity: z
    .string()
    .min(1, 'Quantity is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
      message: 'Quantity must be a positive number',
    }),
  notes: z.string().optional(),
}).refine((data) => data.from_godown_id !== data.to_godown_id, {
  message: 'Source and destination godowns must be different',
  path: ['to_godown_id'],
});

type StockTransferFormData = z.infer<typeof stockTransferSchema>;

interface StockTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: Item;
  godowns: Godown[];
  stockSummary?: ItemStockSummaryResponse;
}

export function StockTransferDialog({
  open,
  onOpenChange,
  item,
  godowns,
  stockSummary,
}: StockTransferDialogProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<StockTransferFormData>({
    resolver: zodResolver(stockTransferSchema),
    defaultValues: {
      quantity: '',
      notes: '',
    },
  });

  const transferMutation = useMutation({
    mutationFn: (data: StockTransferFormData) => 
      transferStock(item.id, {
        from_godown_id: data.from_godown_id,
        to_godown_id: data.to_godown_id,
        quantity: data.quantity,
        notes: data.notes,
      }),
    onSuccess: () => {
      toast.success('Stock transferred successfully');
      queryClient.invalidateQueries({ queryKey: ['item-stock-summary', item.id] });
      queryClient.invalidateQueries({ queryKey: ['current-stock', item.id] });
      form.reset();
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to transfer stock');
    },
  });

  const handleSubmit = async (data: StockTransferFormData) => {
    setIsSubmitting(true);
    try {
      await transferMutation.mutateAsync(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fromGodownId = form.watch('from_godown_id');
  const selectedGodownStock = stockSummary?.stock_by_godown.find(
    (stock) => stock.godown_id === fromGodownId
  );
  const availableQuantity = selectedGodownStock 
    ? parseFloat(selectedGodownStock.quantity) 
    : 0;

  const activeGodowns = godowns.filter((g) => g.is_active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Transfer Stock</DialogTitle>
          <DialogDescription>
            Transfer {item.name} between godowns
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4 items-center">
              <FormField
                control={form.control}
                name="from_godown_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>From Godown</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {activeGodowns.map((godown) => {
                          const stock = stockSummary?.stock_by_godown.find(
                            (s) => s.godown_id === godown.id
                          );
                          const qty = stock ? parseFloat(stock.quantity) : 0;
                          return (
                            <SelectItem 
                              key={godown.id} 
                              value={godown.id.toString()}
                              disabled={qty <= 0}
                            >
                              {godown.name} ({qty.toFixed(2)} {item.unit?.short_name})
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-center">
                <ArrowRight className="h-5 w-5 text-gray-400" />
              </div>

              <FormField
                control={form.control}
                name="to_godown_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>To Godown</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select destination" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {activeGodowns.map((godown) => (
                          <SelectItem 
                            key={godown.id} 
                            value={godown.id.toString()}
                            disabled={godown.id === fromGodownId}
                          >
                            {godown.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantity to Transfer</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      {...field}
                    />
                  </FormControl>
                  {fromGodownId && (
                    <FormDescription>
                      Available: {availableQuantity.toFixed(2)} {item.unit?.short_name}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any notes about this transfer..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {fromGodownId && form.watch('quantity') && (
              <Alert>
                <AlertDescription>
                  {parseFloat(form.watch('quantity')) > availableQuantity ? (
                    <span className="text-destructive">
                      Insufficient stock! Only {availableQuantity.toFixed(2)} {item.unit?.short_name} available.
                    </span>
                  ) : (
                    <span>
                      Transferring {parseFloat(form.watch('quantity')).toFixed(2)} {item.unit?.short_name} from{' '}
                      {activeGodowns.find((g) => g.id === fromGodownId)?.name} to{' '}
                      {form.watch('to_godown_id') && 
                        activeGodowns.find((g) => g.id === form.watch('to_godown_id'))?.name}
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={
                  isSubmitting || 
                  !form.watch('quantity') ||
                  parseFloat(form.watch('quantity')) > availableQuantity
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Transferring...
                  </>
                ) : (
                  'Transfer Stock'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}